var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, css, LitElement, render } from 'lit';
import { property } from 'lit/decorators.js';
import { Directive, directive } from 'lit/directive.js';
import { computePosition, autoPlacement, offset, shift } from '@floating-ui/dom';
import { customElement } from '../Internals';
// Events to turn on/off the tooltip
const enterEvents = ['mouseenter', 'focus'];
const leaveEvents = ['mouseleave', 'blur', 'keydown', 'click'];
let PICToolTip = class PICToolTip extends LitElement {
    // Lazy creation
    static lazy(target, callback) {
        const createTooltip = () => {
            const tooltip = document.createElement('pic-tooltip');
            callback(tooltip);
            target.parentNode.insertBefore(tooltip, target.nextSibling);
            tooltip.show();
            // We only need to create the tooltip once, so ignore all future events.
            enterEvents.forEach((eventName) => target.removeEventListener(eventName, createTooltip));
        };
        enterEvents.forEach((eventName) => target.addEventListener(eventName, createTooltip));
    }
    constructor() {
        super();
        this.allowedPlacements = ['left', 'right', 'top', 'bottom'];
        // Attribute for styling "showing"
        this.showing = false;
        // Position offset
        this.offset = 4;
        // Target for which to show tooltip
        this._target = null;
        this.show = () => {
            this.style.cssText = '';
            computePosition(this.target, this, {
                strategy: 'fixed',
                middleware: [
                    offset(this.offset),
                    shift(),
                    autoPlacement({ allowedPlacements: this.allowedPlacements }),
                ],
            }).then(({ x, y }) => {
                this.style.left = `${x}px`;
                this.style.top = `${y}px`;
            });
            this.showing = true;
        };
        this.hide = () => {
            this.showing = false;
        };
        this.finishHide = () => {
            if (!this.showing) {
                this.style.display = 'none';
            }
        };
        // Finish hiding at end of animation
        this.addEventListener('transitionend', this.finishHide);
    }
    connectedCallback() {
        var _a;
        super.connectedCallback();
        // Setup target if needed
        (_a = this.target) !== null && _a !== void 0 ? _a : (this.target = this.previousElementSibling);
        // Ensure hidden at start
        this.finishHide();
    }
    get target() {
        return this._target;
    }
    set target(target) {
        // Remove events from existing target
        if (this.target) {
            enterEvents.forEach((name) => this.target.removeEventListener(name, this.show));
            leaveEvents.forEach((name) => this.target.removeEventListener(name, this.hide));
        }
        if (target) {
            // Add events to new target
            enterEvents.forEach((name) => target.addEventListener(name, this.show));
            leaveEvents.forEach((name) => target.addEventListener(name, this.hide));
        }
        this._target = target;
    }
    render() {
        return html `<slot></slot>`;
    }
};
PICToolTip.styles = css `
        :host {
            /* Position fixed to help ensure the tooltip is "on top" */
            position: fixed;
            z-index: 50;
            border: 1px solid var(--pic-primaryColor, #003767);
            
            font-family: 'Montserrat';
            font-style: normal;
            font-weight: normal;
            font-size: 12px;
            line-height: 16px;
            letter-spacing: 0.4px;
            background: var(--pic-primaryColor, #003767);
            color: var(--pic-onPrimaryHighColor, #ffffff);
            padding: 4px;

            border-radius: 4px;
            display: inline-block;
            pointer-events: none;
         
            box-shadow: 0px 24px 38px rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);

            /* Animate in */
            opacity: 0;
            transform: scale(0.75);
            transition: opacity, transform;
            transition-duration: 0.33s;
        }

        :host([showing]) {
            opacity: 1;
            transform: scale(1);
        }
        ::slotted(*) {
            white-space: pre;
        }
    `;
__decorate([
    property({ type: Array })
], PICToolTip.prototype, "allowedPlacements", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], PICToolTip.prototype, "showing", void 0);
PICToolTip = __decorate([
    customElement('pic-tooltip')
], PICToolTip);
export { PICToolTip };
class TooltipDirective extends Directive {
    constructor() {
        super(...arguments);
        this.didSetupLazy = false;
    }
    render(tooltipContent = '') { }
    update(part, [tooltipContent]) {
        this.tooltipContent = tooltipContent;
        this.part = part;
        if (!this.didSetupLazy) {
            this.setupLazy();
        }
        if (this.tooltip) {
            this.renderTooltipContent();
        }
    }
    setupLazy() {
        this.didSetupLazy = true;
        PICToolTip.lazy(this.part.element, (tooltip) => {
            this.tooltip = tooltip;
            this.renderTooltipContent();
        });
    }
    renderTooltipContent() {
        render(this.tooltipContent, this.tooltip, this.part.options);
    }
}
export const tooltip = directive(TooltipDirective);
//# sourceMappingURL=PICToolTip.js.map